import {
    type Certificate,
    type CertificateCompanyBranch,
    CertificateScopeMaxLength,
    type CertificateStatus,
    certificateStatuses,
    setCertificates,
    setIsCertificateInstancesRequested,
    setIsCompaniesRequested,
} from '../../../Store/DataSlice'
import './CertificateEdit.scss'
import {useEffect, useMemo, useState} from 'react'
import {TextInput} from '../../../Components/TextInput/TextInput'
import {useLocalization} from '../../../Hooks/useLocalization'
import {Button} from '../../../Components/Button/Button'
import {useDispatch, useSelector} from 'react-redux'
import {setDestinationView, showPopup} from '../../../Store/InterfaceSlice'
import {type RootState} from '../../../Store/Store'
import {getAuthorizationHeader} from '../../../Orchestration/Authentication'
import {DateInput} from '../../../Components/DateInput/DateInput'
import {CheckboxOption} from '../../../Components/CheckboxOption/CheckboxOption'
import {formatCompanyBranch} from '../../../Utils/Formatting'
import {getNaceCodeText, getNaceSelectOptions, NaceCodes,} from '../../../Utils/NaceCodes'
import {MultiSelect} from '../../../Components/MultiSelect/MultiSelect'
import {SingleSelect} from '../../../Components/SingleSelect/SingleSelect'
import {oneMonthInMs, oneYearInMs} from '../../../Utils/Constants'
import {LoadingSpinner} from '../../../Components/LoadingSpinner/LoadingSpinner'
import {sortResultString} from '../../../Utils/Arrays'
import {cloneDeep} from '../../../Utils/Functions'
import {isEmpty} from '../../../Utils/Validations'

interface CertificateEditProps {
    uuid: string
}

export function CertificateEdit({uuid}: CertificateEditProps) {
    const dispatch = useDispatch()
    const localization = useLocalization('certificate-add')
    const countryLocalization = useLocalization('country')
    const certificateStatusesLocalization = useLocalization(
        'certificate-statuses'
    )
    const [allNaceSelectOptions] = useState(getNaceSelectOptions())
    const user = useSelector((state: RootState) => state.authentication.user)
    const [now] = useState(Date.now())
    // const earliestIssueDate = undefined // now - oneYearInMs * 3
    const isAdmin = user?.isAdmin ?? false

    const isCompaniesRequested = useSelector(
        (state: RootState) => state.data.isCompaniesRequested
    )
    useEffect(() => {
        if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
    }, [isCompaniesRequested, dispatch])

    const allCompanies = useSelector((state: RootState) => state.data.companies)

    const isCertificateInstancesRequested = useSelector(
        (state: RootState) => state.data.isCertificateInstancesRequested
    )
    useEffect(() => {
        if (!isCertificateInstancesRequested)
            dispatch(setIsCertificateInstancesRequested(true))
    }, [isCertificateInstancesRequested, dispatch])

    const allCertificateInstances = useSelector(
        (state: RootState) => state.data.certificateInstances
    )
    const allCertificateTypes = useSelector(
        (state: RootState) => state.data.certificateTypes
    )

    const allCertificates = useSelector(
        (state: RootState) => state.data.certificates
    )
    const baseCertificate = useMemo(
        () => allCertificates?.find((c) => c.uuid === uuid),
        [allCertificates, uuid]
    )
    const certificateInstanceCertificates = useMemo(
        () =>
            allCertificates?.filter(
                (c) =>
                    baseCertificate?.certificateInstanceVatNumber ===
                    c.certificateInstanceVatNumber
            ),
        [allCertificates, baseCertificate?.certificateInstanceVatNumber]
    )

    const [certificateChange, setCertificateChange] = useState<
        Partial<Certificate>
    >({})
    const certificate = useMemo(
        () =>
            baseCertificate !== undefined
                ? {...baseCertificate, ...certificateChange}
                : undefined,
        [baseCertificate, certificateChange]
    )

    const originalCertificateStatus = baseCertificate?.status;

    const certificateStatusWarning = function (certificate: Certificate) {

        if (originalCertificateStatus !== 'Withdrawn' && certificate.status === 'Withdrawn') {

            const text = localization.text('statusWithdrawnFinal', 'De status ingetrokken kan nooit meer veranderen, weet u zeker dat u deze op status Ingetokken wil zetten ?');

            return (
                <div className="input-warning">{text}</div>
            );

        }
        return null;
    }

    const [companyBranchVatToAdd, setCompanyBranchVatToAdd] = useState('')
    const [restoredSince, setRestoredSince] = useState(Date.now())

    const [validationErrors, setValidationErrors] = useState<validationError[]>(
        []
    )
    useEffect(() => {
        const newValidationErrors: validationError[] = []
        if (!certificate) return
        if (!certificate.certificateNumber)
            newValidationErrors.push({
                property: 'certificateNumber',
                type: 'isRequired',
            })

        if (
            certificate.certificateNumber &&
            certificateInstanceCertificates.some(
                (c) =>
                    c.uuid !== certificate.uuid &&
                    c.certificateNumber === certificate.certificateNumber.trim()
            )
        )
            newValidationErrors.push({
                property: 'certificateNumber',
                type: 'isDuplicate',
            })

        if (
            certificate.naceCodes.length === 0 &&
            (!certificate.scope || certificate.scope.length === 0)
        )
            newValidationErrors.push({property: 'naceCodes', type: 'isRequired'})
        const obsoleteNaceCodes = certificate.naceCodes.filter(
            (nc) => !NaceCodes.some((naceCode) => naceCode.code === nc)
        )
        if (obsoleteNaceCodes.length > 0)
            newValidationErrors.push({
                property: 'naceCodes',
                type: 'isObsolete',
                extraData: {codes: obsoleteNaceCodes.join(', ')},
            })
        if (!certificate.scope)
            newValidationErrors.push({property: 'scope', type: 'isRequired'})
        if (!certificate.status)
            newValidationErrors.push({property: 'status', type: 'isRequired'})
        if (!certificate.type)
            newValidationErrors.push({property: 'type', type: 'isRequired'})
        if (!isEmpty(certificate.type)) {
            const certificateType = allCertificateTypes.find(
                (ct) => ct.name === certificate.type
            )
            if (
                certificateType &&
                certificateType.naceLimitation?.length > 0 &&
                certificate.companyBranches?.length > 0
            ) {
                if (
                    allCompanies
                        .filter((c) =>
                            certificate.companyBranches.some(
                                (cb) => cb.vatNumber === c.vatNumber
                            )
                        )
                        .some((c) => !certificateType.naceLimitation.includes(c.naceCode))
                )
                    newValidationErrors.push({
                        property: 'type',
                        type: 'requireNaceCode',
                        extraData: {
                            naceLimitation: certificateType.naceLimitation.join(', '),
                        },
                    })
            }
        }

        if (!certificate.validFrom)
            newValidationErrors.push({
                property: 'validFrom',
                type: 'isRequired',
            })
        if (!certificate.validUntil)
            newValidationErrors.push({
                property: 'validUntil',
                type: 'isRequired',
            })
        if (!certificate.issueDate)
            newValidationErrors.push({
                property: 'issueDate',
                type: 'isRequired',
            })

        if (certificate.status === 'Prolonged' && certificate.extendedUntil == null)
            newValidationErrors.push({
                property: 'extendedUntil',
                type: 'isRequired',
            })

        if (certificate.companyBranches.length === 0)
            newValidationErrors.push({
                property: 'companyBranches',
                type: 'isRequired',
            })
        if (
            certificate.isGroupCertificate &&
            certificate.companyBranches.some(
                (cb) => cb.branchNumbers.length === 0 && cb.headquarters !== true
            )
        )
            newValidationErrors.push({
                property: 'companyBranchVatNumbers',
                type: 'isRequired',
            })

        if (certificate.status === 'Suspended' && certificate.suspendedFrom == null)
            newValidationErrors.push({
                property: 'suspendedFrom',
                type: 'isRequired',
            })

        if (certificate.status === 'Withdrawn' && certificate.withdrawnFrom == null)
            newValidationErrors.push({
                property: 'withdrawnFrom',
                type: 'isRequired',
            })

        if (
            (baseCertificate?.status === 'Suspended' ||
                baseCertificate?.status === 'Withdrawn') &&
            certificate.status !== baseCertificate?.status &&
            restoredSince == null
        )
            newValidationErrors.push({
                property: 'restoredSince',
                type: 'isRequired',
            })

        if (certificate) {

            const requiredCerts = allCertificateTypes.find(type => certificate.type === type.name)?.requiredCertificateNumbers ?? [];

            if (requiredCerts.length > 0) {

                const compsToAdd = (certificate.companyBranches ?? []).map(cb => cb.vatNumber);

                for(const compToAdd of compsToAdd){

                    const companyCerts = allCertificates
                        .filter(c => c.companyBranches.some((cb) => compToAdd === cb.vatNumber))
                        .map(c => c.type);

                    if (requiredCerts.some(c => !companyCerts.includes(c))) {
                        newValidationErrors.push({
                            property: 'type',
                            type: 'requireCertType',
                            extraData: {
                                requireCertTypes: requiredCerts.join(', '),
                            },
                        });

                    }

                }

            }
        }

        console.log('ValidationErrors: ', JSON.stringify(newValidationErrors))
        setValidationErrors(newValidationErrors)
    }, [
        certificate,
        certificateInstanceCertificates,
        baseCertificate,
        restoredSince,
        allCertificateTypes,
        allCompanies,
        allCertificates
    ])

    const [saveInProgress, setSaveInProgress] = useState(false)
    const saveCertificate = () => {
        setSaveInProgress(true)
        if (!certificate) return
        const cleanedCertificate = {...certificate}

        if (!cleanedCertificate.isGroupCertificate) {
            cleanedCertificate.companyBranches =
                cleanedCertificate.companyBranches.map((cb) => ({
                    vatNumber: cb.vatNumber,
                    branchNumbers: [],
                }))
        }

        cleanedCertificate.certificateNumber =
            cleanedCertificate.certificateNumber.trim()

        if (cleanedCertificate.status !== baseCertificate?.status) {
            if (
                baseCertificate?.status === 'Suspended' &&
                baseCertificate.suspendedFrom != null
            ) {
                cleanedCertificate.deniedPeriods = [
                    ...(cleanedCertificate.deniedPeriods ?? []),
                ]
                cleanedCertificate.deniedPeriods.push({
                    deniedFrom: baseCertificate.suspendedFrom,
                    deniedStatus: 'Suspended',
                    deniedUntil: restoredSince,
                })
            }
            if (
                baseCertificate?.status === 'Withdrawn' &&
                baseCertificate.withdrawnFrom != null
            ) {
                cleanedCertificate.deniedPeriods = [
                    ...(cleanedCertificate.deniedPeriods ?? []),
                ]

                cleanedCertificate.deniedPeriods.push({
                    deniedFrom: baseCertificate.withdrawnFrom,
                    deniedStatus: 'Withdrawn',
                    deniedUntil: restoredSince,
                })
            }
        }
        if (cleanedCertificate.status !== 'Suspended')
            cleanedCertificate.suspendedFrom = null as unknown as undefined // to ensure we clear it
        if (cleanedCertificate.status !== 'Withdrawn')
            cleanedCertificate.withdrawnFrom = null as unknown as undefined // to ensure we clear it

        fetch(
            `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-add`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthorizationHeader(),
                },
                body: JSON.stringify({
                    certificate: cleanedCertificate,
                }),
            }
        )
            .then(() => {
                dispatch(
                    setCertificates([
                        ...allCertificates.filter(
                            (c) => c.uuid !== cleanedCertificate.uuid
                        ),
                        cleanedCertificate,
                    ])
                )
                setTimeout(() => {
                    dispatch(setDestinationView({destinationView: 'certificate-list'}))
                }, 1)
            })
            .catch((err) => {
                console.warn(`fetch certificate-add failed: `, err)
            })
    }

    if (!certificate) return null

    const getCertTypeHelperText = () => {

        if (validationErrors.find((ve) => ve.property === 'type')?.type === 'isRequired') {
            return localization.text('type-missing', 'Selecteer een geldig type');
        }

        if (validationErrors.find((ve) => ve.property === 'type')?.type === 'requireNaceCode') {
            return localization.text(
                'type-required-nace-code',
                'Dit type is enkel toepasbaar op ondernemingen met NACE code {naceLimitation}',
                {
                    naceLimitation: validationErrors.find((ve) => ve.property === 'type')?.extraData?.naceLimitation ?? '',
                }
            );
        }

        if (validationErrors.find((ve) => ve.property === 'type')?.type === 'requireCertType') {
            return localization.text(
                'type-required-cert-type',
                'Dit type is enkel toepasbaar op ondernemingen met certificaat type(s) {certTypes}',
                {
                    certTypes: validationErrors.find((ve) => ve.property === 'type')?.extraData?.requireCertTypes ?? '',
                }
            );
        }

        return undefined;

    };

    return (
        <div className="certificate-edit">
            {saveInProgress ? (
                <div className="save-in-progress">
                    <LoadingSpinner color="dark"/>
                    {localization.text(
                        'certificate-save-in-progress',
                        'Het certificaat word momenteel opgeslagen.'
                    )}
                </div>
            ) : !baseCertificate ? (
                <div className="loading">
                    <LoadingSpinner color="dark"/>
                    {localization.text(
                        'certificate-is-loading',
                        'Het certificaat word momenteel ingeladen.'
                    )}
                </div>
            ) : (
                <>
                    <SingleSelect
                        label={localization.text('type', 'Type')}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            setCertificateChange((previous) => ({
                                ...previous,
                                type: newValue,
                            }))
                        }}
                        value={certificate.type}
                        isValid={!validationErrors.some((ve) => ve.property === 'type')}
                        helperText={getCertTypeHelperText()}
                        items={(allCertificateTypes ?? [])
                            .filter((ct) => ct.isActive)
                            .map((ct) => ({
                                value: ct.name,
                                label: ct.name,
                            }))
                            .sort((a, b) => sortResultString(a.label, b.label))}
                    />

                    <TextInput
                        label={localization.text('certificateNumber', 'Certificaat nummer')}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            setCertificateChange((previous) => ({
                                ...previous,
                                certificateNumber: newValue,
                            }))
                        }}
                        value={certificate.certificateNumber}
                        isValid={
                            !validationErrors.some(
                                (ve) => ve.property === 'certificateNumber'
                            )
                        }
                        helperText={
                            validationErrors.find((ve) => ve.property === 'certificateNumber')
                                ?.type === 'isRequired'
                                ? localization.text(
                                    'certificateNumber-missing',
                                    'Geef een geldige certificaat nummer in'
                                )
                                : validationErrors.find(
                                    (ve) => ve.property === 'certificateNumber'
                                )?.type === 'isDuplicate'
                                    ? localization.text(
                                        'certificateNumber-is-duplicate',
                                        'Dit certificaat nummer is al in onze database'
                                    )
                                    : undefined
                        }
                    />
                    <MultiSelect
                        label={localization.text('naceCodes', 'NACE codes')}
                        isReadOnly={saveInProgress}
                        items={allNaceSelectOptions}
                        onChange={(items) => {
                            const newItems = items.filter((item) =>
                                allNaceSelectOptions.some(
                                    (naceOption) => naceOption.value === item
                                )
                            )
                            setCertificateChange((p) => ({
                                ...p,
                                naceCodes: newItems,
                                scope: newItems
                                    .map((nc) => getNaceCodeText(nc))
                                    .join(', ')
                                    .substring(0, CertificateScopeMaxLength),
                            }))
                        }}
                        values={certificate.naceCodes}
                        isValid={
                            !validationErrors.some((ve) => ve.property === 'naceCodes')
                        }
                        helperText={
                            validationErrors.some(
                                (ve) => ve.property === 'naceCodes' && ve.type === 'isObsolete'
                            )
                                ? localization.text(
                                    'naceCodes-isObsolete',
                                    'Nace codes zijn vervallen: {codes}',
                                    validationErrors.find(
                                        (ve) =>
                                            ve.property === 'naceCodes' && ve.type === 'isObsolete'
                                    )?.extraData
                                )
                                : validationErrors.some(
                                    (ve) =>
                                        ve.property === 'naceCodes' && ve.type === 'isRequired'
                                )
                                    ? localization.text(
                                        'naceCodes-missing',
                                        'Geef minstens één geldige NACE code in'
                                    )
                                    : undefined
                        }
                    />
                    <TextInput
                        label={localization.text('scope', 'Scope')}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            setCertificateChange((previous) => ({
                                ...previous,
                                scope: newValue.substring(0, CertificateScopeMaxLength),
                            }))
                        }}
                        value={certificate.scope}
                        isValid={!validationErrors.some((ve) => ve.property === 'scope')}
                        helperText={
                            validationErrors.find((ve) => ve.property === 'scope')?.type ===
                            'isRequired'
                                ? localization.text(
                                    'scope-missing',
                                    'Geef een geldige scope in'
                                )
                                : CertificateScopeMaxLength - certificate.scope.length < 250
                                    ? localization.text(
                                        'scope-max-length-helper',
                                        '{remainingLength}/{maxLength} tekens resterend',
                                        {
                                            remainingLength: `${
                                                CertificateScopeMaxLength - certificate.scope.length
                                            }`,
                                            maxLength: `${CertificateScopeMaxLength}`,
                                        }
                                    )
                                    : undefined
                        }
                        multiLine={true}
                    />

                    <CheckboxOption
                        label={localization.text(
                            'isGroupCertificate',
                            'Is multi-vestiging'
                        )}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            setCertificateChange((previous) => ({
                                ...previous,
                                isGroupCertificate: newValue,
                                ...(!newValue
                                    ? {
                                        companyBranches: certificate?.companyBranches?.map(
                                            (cb) => ({
                                                vatNumber: cb.vatNumber,
                                                branchNumbers: [],
                                            })
                                        ),
                                    }
                                    : {}),
                            }))
                        }}
                        value={certificate.isGroupCertificate}
                    />

                    <div className="company-branches">
                        <div className="label">
                            {localization.text(
                                'companyBranches',
                                'Ondernemingen en vestigingen'
                            )}
                        </div>
                        {certificate.companyBranches.map((cb) => {
                            const company = allCompanies.find(
                                (c) => c.vatNumber === cb.vatNumber
                            )
                            if (!company) return null
                            return (
                                <div className="company-option" key={`${company.vatNumber}`}>
                                    <div className="company-option-name">
                                        <div className="company-remove">
                                            <Button
                                                size="small"
                                                importance="secondary"
                                                text="❌"
                                                onClick={() => {
                                                    const companyBranches = [
                                                        ...certificate.companyBranches,
                                                    ].filter((cb) => cb.vatNumber !== company.vatNumber)
                                                    setCertificateChange((p) => ({
                                                        ...p,
                                                        companyBranches,
                                                    }))
                                                }}
                                            />
                                        </div>
                                        <div className="company-name">{company.name}</div>
                                    </div>
                                    {certificate.isGroupCertificate && (
                                        <>
                                            <div className="company-branches">
                                                <div className="company-branch">
                                                    <CheckboxOption
                                                        value={cb.headquarters ?? false}
                                                        valueName={localization.text(
                                                            'headquarter',
                                                            'Hoofdzetel'
                                                        )}
                                                        onChange={(newValue) => {
                                                            const companyBranches = cloneDeep(
                                                                certificate.companyBranches
                                                            )
                                                            const companyBranch = companyBranches.find(
                                                                (companyBranch) =>
                                                                    companyBranch.vatNumber === cb.vatNumber
                                                            )
                                                            if (!companyBranch) return
                                                            companyBranch.headquarters = newValue
                                                            setCertificateChange((p) => ({
                                                                ...p,
                                                                companyBranches,
                                                            }))
                                                        }}
                                                    />
                                                </div>
                                                {company.branches.map((b) => {
                                                    const isSelected = cb.branchNumbers?.includes(
                                                        b.number
                                                    )
                                                    return (
                                                        <div
                                                            className="company-branch"
                                                            key={`${company.vatNumber}-${b.number}`}
                                                        >
                                                            <CheckboxOption
                                                                value={isSelected}
                                                                valueName={formatCompanyBranch(
                                                                    b,
                                                                    countryLocalization
                                                                )}
                                                                onChange={(newValue) => {
                                                                    const companyBranches: CertificateCompanyBranch[] =
                                                                        JSON.parse(
                                                                            JSON.stringify(
                                                                                certificate.companyBranches
                                                                            )
                                                                        )
                                                                    const companyBranch = companyBranches.find(
                                                                        (companyBranch) =>
                                                                            companyBranch.vatNumber === cb.vatNumber
                                                                    )
                                                                    if (!companyBranch) return
                                                                    if (newValue) {
                                                                        companyBranch.branchNumbers.push(b.number)
                                                                    } else {
                                                                        const newBranchNumber =
                                                                            companyBranch.branchNumbers.filter(
                                                                                (bn) => bn !== b.number
                                                                            )
                                                                        companyBranch.branchNumbers =
                                                                            newBranchNumber
                                                                    }
                                                                    setCertificateChange((p) => ({
                                                                        ...p,
                                                                        companyBranches,
                                                                    }))
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                })}
                                                {cb.branchNumbers.length === 0 &&
                                                    cb.headquarters !== true && (
                                                        <div className="select-branch-warning">
                                                            {localization.text(
                                                                'companyBranches-not-selected',
                                                                'Kies minstens één vestiging'
                                                            )}
                                                        </div>
                                                    )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )
                        })}
                        <div className="add-company-branch">
                            <SingleSelect
                                label={localization.text(
                                    'addCompanyBranch',
                                    'Selecteer onderneming of typ BTW nummer'
                                )}
                                items={allCompanies
                                    .filter(
                                        (company) =>
                                            !certificate.companyBranches.some(
                                                (cb) => cb.vatNumber === company.vatNumber
                                            )
                                    )
                                    .map((companies) => ({
                                        value: companies.vatNumber,
                                        label: `${companies.vatNumber}: ${companies.name}`,
                                    }))
                                    .sort((a, b) => sortResultString(a.label, b.label))}
                                isReadOnly={saveInProgress}
                                onChange={(newValue) => {
                                    setCompanyBranchVatToAdd(newValue)
                                }}
                                value={companyBranchVatToAdd}
                                isValid={
                                    !validationErrors.some(
                                        (ve) => ve.property === 'companyBranches'
                                    )
                                }
                                helperText={
                                    validationErrors.find(
                                        (ve) => ve.property === 'companyBranches'
                                    )?.type === 'isRequired'
                                        ? localization.text(
                                            'companyBranches-missing',
                                            'Kies minstens één onderneming en vestiging'
                                        )
                                        : undefined
                                }
                            />
                            <div className="buttons-row">
                                <Button
                                    importance={'secondary'}
                                    text={localization.text(
                                        'add-companyBranch',
                                        'Selecteer onderneming'
                                    )}
                                    disabled={
                                        !companyBranchVatToAdd ||
                                        (!certificate.isGroupCertificate &&
                                            certificate.companyBranches.length >= 0)
                                    }
                                    onClick={() => {
                                        if (
                                            certificate.companyBranches.some(
                                                (cb) => cb.vatNumber === companyBranchVatToAdd
                                            )
                                        )
                                            return
                                        const company = allCompanies.find(
                                            (c) => c.vatNumber === companyBranchVatToAdd
                                        )
                                        if (!company) return
                                        setCertificateChange((p) => ({
                                            ...p,
                                            companyBranches: [
                                                ...certificate.companyBranches,
                                                {
                                                    vatNumber: company.vatNumber,
                                                    branchNumbers:
                                                        company.branches.length === 1
                                                            ? [company.branches[0].number]
                                                            : [],
                                                },
                                            ],
                                        }))
                                    }}
                                />
                                <Button
                                    size="regular"
                                    importance="secondary"
                                    text={localization.text(
                                        'create-new-company',
                                        'Maak nieuwe onderneming aan'
                                    )}
                                    onClick={() => dispatch(showPopup({type: 'company-add'}))}
                                />
                            </div>
                        </div>
                    </div>

                    <SingleSelect
                        label={localization.text('status', 'Status')}
                        items={certificateStatuses.map((status) => ({
                            value: status,
                            label: status
                                ? certificateStatusesLocalization.text(status, status)
                                : '',
                        }))}
                        isReadOnly={
                            saveInProgress || (certificate.status === 'Withdrawn' && !isAdmin)
                        }
                        onChange={(newValue) => {
                            const newStatus = newValue as CertificateStatus
                            setCertificateChange((previous) => ({
                                ...previous,
                                status: newStatus,
                                ...(newStatus === 'Suspended' && previous.suspendedFrom == null
                                    ? {suspendedFrom: Date.now()}
                                    : {}),
                                ...(newStatus === 'Withdrawn' && previous.withdrawnFrom == null
                                    ? {withdrawnFrom: Date.now()}
                                    : {}),
                            }))
                        }}
                        value={certificate.status}
                        isValid={!validationErrors.some((ve) => ve.property === 'status')}
                        helperText={
                            validationErrors.find((ve) => ve.property === 'status')?.type ===
                            'isRequired'
                                ? localization.text('status-missing', 'Kies een status')
                                : undefined
                        }
                    />

                    { certificateStatusWarning(certificate) }

                    {isAdmin ? (
                        <SingleSelect
                            label={localization.text(
                                'certificateInstances',
                                'Certificatie instantie'
                            )}
                            items={allCertificateInstances
                                .map((certificateInstances) => ({
                                    value: certificateInstances.vatNumber,
                                    label: certificateInstances.name,
                                }))
                                .sort((a, b) => sortResultString(a.label, b.label))}
                            isReadOnly={saveInProgress}
                            onChange={(newValue) => {
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    certificateInstanceVatNumber: newValue,
                                }))
                            }}
                            value={certificate.certificateInstanceVatNumber}
                            isRequired={true}
                        />
                    ) : (
                        <div className="display-field">
                            <div className="display-label">
                                {localization.text(
                                    'certificateInstances',
                                    'Certificatie instantie'
                                )}
                            </div>
                            <div className="display-value">
                                {allCertificateInstances.find(
                                    (c) =>
                                        c.vatNumber === certificate.certificateInstanceVatNumber
                                )?.name ?? ''}
                            </div>
                        </div>
                    )}

                    {/* <div className="display-field">
            <div className="display-label">
              {localization.text('issueDate', 'Uitgereikt op')}
            </div>
            <div className="display-value">
              {toBelgianDate(certificate.issueDate)}
            </div>
          </div> */}
                    <DateInput
                        label={localization.text('issueDate', 'Uitgereikt op')}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            if (newValue !== undefined)
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    issueDate: newValue,
                                }))
                        }}
                        value={certificate.issueDate}
                        isValid={
                            !validationErrors.some((ve) => ve.property === 'issueDate')
                        }
                        helperText={
                            validationErrors.find((ve) => ve.property === 'issueDate')
                                ?.type === 'isRequired'
                                ? localization.text(
                                    'extendedUntil-missing',
                                    'Geef een geldige datum in'
                                )
                                : undefined
                        }
                        // min={isAdmin ? undefined : earliestIssueDate}
                        max={isAdmin ? undefined : now}
                    />

                    {/* <div className="display-field">
            <div className="display-label">
              {localization.text('validFrom', 'Geldig van')}
            </div>
            <div className="display-value">
              {toBelgianDate(certificate.validFrom)}
            </div>
          </div> */}

                    <DateInput
                        label={localization.text('validFrom', 'Geldig van')}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            if (newValue !== undefined)
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    validFrom: newValue,
                                    validUntil: newValue + 3 * oneYearInMs,
                                }))
                        }}
                        value={certificate.validFrom}
                        isValid={
                            !validationErrors.some((ve) => ve.property === 'validFrom')
                        }
                        helperText={
                            validationErrors.find((ve) => ve.property === 'validFrom')
                                ?.type === 'isRequired'
                                ? localization.text(
                                    'extendedUntil-missing',
                                    'Geef een geldige datum in'
                                )
                                : undefined
                        }
                        // min={isAdmin ? undefined : earliestIssueDate}
                        max={isAdmin ? undefined : now + 4 * oneMonthInMs}
                    />

                    {/* <div className="display-field">
            <div className="display-label">
              {localization.text('validUntil', 'Geldig tot')}
            </div>
            <div className="display-value">
              {toBelgianDate(certificate.validUntil)}
            </div>
          </div> */}

                    <DateInput
                        label={localization.text('validUntil', 'Geldig tot')}
                        isReadOnly={saveInProgress}
                        onChange={(newValue) => {
                            if (newValue !== undefined)
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    validUntil: newValue,
                                }))
                        }}
                        value={certificate.validUntil}
                        isValid={
                            !validationErrors.some((ve) => ve.property === 'validUntil')
                        }
                        helperText={
                            validationErrors.find((ve) => ve.property === 'validUntil')
                                ?.type === 'isRequired'
                                ? localization.text(
                                    'extendedUntil-missing',
                                    'Geef een geldige datum in'
                                )
                                : undefined
                        }
                        min={isAdmin ? undefined : certificate.validFrom}
                        max={isAdmin ? undefined : certificate.validFrom + 3 * oneYearInMs}
                    />

                    {certificate.status === 'Prolonged' && (
                        <DateInput
                            label={localization.text('extendedUntil', 'Verlengd tot')}
                            isReadOnly={saveInProgress}
                            onChange={(newValue) => {
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    extendedUntil: newValue,
                                }))
                            }}
                            value={certificate.extendedUntil}
                            isValid={
                                !validationErrors.some((ve) => ve.property === 'extendedUntil')
                            }
                            helperText={
                                validationErrors.find((ve) => ve.property === 'extendedUntil')
                                    ?.type === 'isRequired'
                                    ? localization.text(
                                        'extendedUntil-missing',
                                        'Geef een geldige datum in'
                                    )
                                    : undefined
                            }
                            min={isAdmin ? undefined : certificate.validUntil}
                            max={
                                isAdmin ? undefined : certificate.validUntil + 6 * oneMonthInMs
                            }
                        />
                    )}

                    {certificate.status === 'Suspended' && (
                        <DateInput
                            label={localization.text('suspendedFrom', 'Opgeschort sinds')}
                            isReadOnly={saveInProgress}
                            onChange={(newValue) => {
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    suspendedFrom: newValue,
                                }))
                            }}
                            value={certificate.suspendedFrom}
                            isValid={
                                !validationErrors.some((ve) => ve.property === 'suspendedFrom')
                            }
                            helperText={
                                validationErrors.find((ve) => ve.property === 'suspendedFrom')
                                    ?.type === 'isRequired'
                                    ? localization.text(
                                        'suspendedFrom-missing',
                                        'Geef een geldige datum in'
                                    )
                                    : undefined
                            }
                            min={isAdmin ? undefined : certificate.validFrom}
                            max={isAdmin ? undefined : certificate.validUntil}
                        />
                    )}

                    {certificate.status === 'Withdrawn' && (
                        <DateInput
                            label={localization.text('withdrawnFrom', 'Ingetrokken sinds')}
                            isReadOnly={saveInProgress}
                            onChange={(newValue) => {
                                setCertificateChange((previous) => ({
                                    ...previous,
                                    withdrawnFrom: newValue,
                                }))
                            }}
                            value={certificate.withdrawnFrom}
                            isValid={
                                !validationErrors.some((ve) => ve.property === 'withdrawnFrom')
                            }
                            helperText={
                                validationErrors.find((ve) => ve.property === 'withdrawnFrom')
                                    ?.type === 'isRequired'
                                    ? localization.text(
                                        'withdrawnFrom-missing',
                                        'Geef een geldige datum in'
                                    )
                                    : undefined
                            }
                            min={isAdmin ? undefined : certificate.validFrom}
                            max={isAdmin ? undefined : certificate.validUntil}
                        />
                    )}

                    {certificate.status !== baseCertificate.status &&
                        (baseCertificate.status === 'Suspended' ||
                            baseCertificate.status === 'Withdrawn') && (
                            <DateInput
                                label={localization.text('restoredSince', 'Hersteld sinds')}
                                isReadOnly={saveInProgress}
                                onChange={(newValue) => {
                                    setRestoredSince(newValue ?? Date.now())
                                }}
                                value={restoredSince}
                                isValid={
                                    !validationErrors.some(
                                        (ve) => ve.property === 'restoredSince'
                                    )
                                }
                                helperText={
                                    validationErrors.find((ve) => ve.property === 'restoredSince')
                                        ?.type === 'isRequired'
                                        ? localization.text(
                                            'restoredSince-missing',
                                            'Geef een geldige datum in'
                                        )
                                        : undefined
                                }
                                min={
                                    isAdmin
                                        ? undefined
                                        : certificate.suspendedFrom ?? certificate.withdrawnFrom
                                }
                                max={isAdmin ? undefined : certificate.validUntil}
                            />
                        )}

                    <div className="buttons">
                        <Button
                            importance={'primary'}
                            text={localization.text(
                                'safe-edit-button',
                                'Pas certificaat aan'
                            )}
                            onClick={saveCertificate}
                            disabled={
                                validationErrors.filter((ve) => ve.type !== 'isDuplicate')
                                    .length > 0 || saveInProgress
                            }
                        />
                    </div>
                </>
            )}
        </div>
    )
}

interface validationError {
    property: string
    type:
        | 'isRequired'
        | 'format'
        | 'requireNaceCode'
        | 'requireCertType'
        | 'isDuplicate'
        | 'isObsolete'
    extraData?: Record<string, string>
}
